import { useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useTranslate } from '@/hooks/useTranslate';
import { useCancelCheckout } from '@/hooks/useCancelCheckout';
import { cn } from '@/utils/cn';

/**
 * Redirect mode's stand-in for the X button. The checkout owns the whole tab
 * there, so there is no overlay to dismiss and no page behind it — the way out
 * has to be an explicit control under the pay button, and it navigates back to
 * the merchant with `status=cancel`, the same report the X used to make.
 *
 * Mounting is gated by `Payment` (on `features.showBackToStoreLink` plus
 * redirect mode) rather than here, so the cancel-order mutation and
 * redirect-link builder below are only ever created where the link is shown.
 */
export const BackToStoreLink = () => {
  const context = useContext(CheckoutContext);
  const {
    actions: { closeCheckout },
  } = context;
  const { t } = useTranslate();
  const { cancelAndClose, isClosingRef, isPaymentInFlight } = useCancelCheckout(() => closeCheckout(context));

  return (
    <div className="flex w-full items-center justify-center">
      <button
        type="button"
        data-testid="back-to-store-link"
        onClick={cancelAndClose}
        disabled={isClosingRef.current || isPaymentInFlight}
        className={cn(
          'cursor-pointer text-sm font-medium leading-5 text-base-primary underline',
          isPaymentInFlight && 'invisible pointer-events-none'
        )}
        aria-hidden={isPaymentInFlight}
      >
        {t('backToStore')}
      </button>
    </div>
  );
};
